import React, { useState, useEffect, useCallback, useRef } from "react";
import "./FillerGame.css";
import { useLanguage } from "../contexts/LanguageContext";
import Keyboard from "./Keyboard";
import { useFirebase } from "../contexts/FirebaseContext";
import { publishDemoState } from "../utils/demoMode";

// Word lists - 4-6 letter words suitable for children
const ALL_WORDS_EN = [
  "CAT", "DOG", "BIRD", "FISH", "LION", "BEAR", "DEER", "WOLF",
  "TREE", "ROSE", "LEAF", "SEED", "ROOT", "BARK", "STEM", "BUSH",
  "BOOK", "PEN", "DESK", "CHAIR", "TABLE", "LAMP", "DOOR", "WINDOW",
  "SUN", "MOON", "STAR", "CLOUD", "RAIN", "SNOW", "WIND", "STORM",
  "APPLE", "BANANA", "GRAPE", "ORANGE", "PEACH", "BERRY", "MANGO", "LEMON",
  "CAR", "BIKE", "BUS", "TRAIN", "BOAT", "SHIP", "PLANE", "TRUCK",
  "HOUSE", "ROOM", "ROOF", "WALL", "FLOOR", "GARDEN", "FENCE", "GATE",
  "HEART", "HAND", "FOOT", "EYE", "EAR", "NOSE", "MOUTH", "HEAD",
  "WATER", "FIRE", "EARTH", "AIR", "ROCK", "SAND", "DIRT", "GRASS",
  "CAKE", "COOKIE", "BREAD", "MILK", "EGG", "CHEESE", "MEAT", "SOUP",
];

const ALL_WORDS_DE = [
  "KATZE", "HUND", "VOGEL", "FISCH", "LÖWE", "BÄR", "HIRSCH", "WOLF",
  "BAUM", "ROSE", "BLATT", "SAME", "WURZEL", "RINDE", "STAMM", "BUSCH",
  "BUCH", "STIFT", "STUHL", "TISCH", "LAMPE", "TÜR", "FENSTER",
  "SONNE", "MOND", "STERN", "WOLKE", "REGEN", "SCHNEE", "WIND", "STURM",
  "APFEL", "BANANE", "TRAUBE", "ORANGE", "PFIRSICH", "BEERE", "MANGO", "ZITRONE",
  "AUTO", "FAHRRAD", "BUS", "ZUG", "BOOT", "SCHIFF", "FLUGZEUG",
  "HAUS", "ZIMMER", "DACH", "WAND", "BODEN", "GARTEN", "ZAUN", "TOR",
  "HERZ", "HAND", "FUß", "AUGE", "OHR", "NASE", "MUND", "KOPF",
  "WASSER", "FEUER", "ERDE", "LUFT", "STEIN", "SAND", "GRAS",
  "KUCHEN", "KEKS", "BROT", "MILCH", "EI", "KÄSE", "FLEISCH", "SUPPE",
];

// Filter to only 4-6 letter words
const WORD_LIST_EN = ALL_WORDS_EN.filter(word => word.length >= 4 && word.length <= 6);
const WORD_LIST_DE = ALL_WORDS_DE.filter(word => word.length >= 4 && word.length <= 6);

// Get random words from the list based on language
const getRandomWords = (count = 10, language = "en") => {
  const wordList = language === "de" ? WORD_LIST_DE : WORD_LIST_EN;
  const shuffled = [...wordList].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

const FillerGame = ({ onPointsChange, autoStart = false }) => {
  const { t, language } = useLanguage();
  const { recordGameEvent } = useFirebase();
  const [timeLeft, setTimeLeft] = useState(60);
  const [currentWord, setCurrentWord] = useState(null);
  const [wordWithBlank, setWordWithBlank] = useState("");
  const [missingLetter, setMissingLetter] = useState("");
  const [guessedLetters, setGuessedLetters] = useState(new Set());
  const [words, setWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [wrongGuesses, setWrongGuesses] = useState(0);
  const [message, setMessage] = useState("");
  const [score, setScore] = useState(0);
  const [wordCompleted, setWordCompleted] = useState(false);
  const didAutoStartRef = useRef(false);

  useEffect(() => {
    publishDemoState({
      game: "filler",
      ready: gameStarted && !gameOver,
      missingLetter,
      wordWithBlank,
      wordCompleted,
      gameOver,
    });
  }, [gameStarted, gameOver, missingLetter, wordWithBlank, wordCompleted]);

  // Initialize game with random words
  const startGame = useCallback(() => {
    const randomWords = getRandomWords(15, language); // Generate more words than needed
    setWords(randomWords);
    setCurrentWordIndex(0);
    setGameStarted(true);
    setGameOver(false);
    setTimeLeft(60);
    setScore(0);
    setMessage("");
    loadNextWord(randomWords, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    if (!autoStart || didAutoStartRef.current) return;
    didAutoStartRef.current = true;
    startGame();
  }, [autoStart, startGame]);

  // Load next word with a random letter removed
  const loadNextWord = useCallback((wordsList, index) => {
    if (index >= wordsList.length) {
      // Out of words, game continues until time runs out
      return;
    }

    const word = wordsList[index];
    const randomIndex = Math.floor(Math.random() * word.length);
    const letter = word[randomIndex];
    
    const wordWithBlank = word
      .split("")
      .map((char, i) => (i === randomIndex ? "_" : char))
      .join(" ");

    setCurrentWord(word);
    setWordWithBlank(wordWithBlank);
    setMissingLetter(letter);
    setGuessedLetters(new Set());
    setAttempts(0);
    setWrongGuesses(0);
    setMessage("");
    setWordCompleted(false);
  }, []);

  // Play countdown beep sound
  const playCountdownBeep = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.type = "sine";

      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (error) {
      // Silently fail if audio context creation fails
      console.error("Audio context error:", error);
    }
  }, []);

  // Play time up sound
  const playTimeUpSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const now = audioContext.currentTime;

      // Create a longer, lower-pitched beep for time up
      const playBeep = (frequency, time, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(frequency, time);
        oscillator.type = "sine";

        gainNode.gain.setValueAtTime(0.3, time);
        gainNode.gain.exponentialRampToValueAtTime(0.01, time + duration);

        oscillator.start(time);
        oscillator.stop(time + duration);
      };

      // Play two descending beeps
      playBeep(400, now, 0.3);
      playBeep(300, now + 0.4, 0.5);
    } catch (error) {
      // Silently fail if audio context creation fails
      console.error("Audio context error:", error);
    }
  }, []);

  // Start countdown timer
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    if (timeLeft <= 0) {
      setGameOver(true);
      setMessage(t("timesUp"));
      playTimeUpSound();
      return;
    }

    // Play beep for last 10 seconds
    if (timeLeft <= 10 && timeLeft > 0) {
      playCountdownBeep();
    }

    const timer = setTimeout(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, gameStarted, gameOver, playCountdownBeep, playTimeUpSound]);

  // Load first word when words are set
  useEffect(() => {
    if (words.length > 0 && gameStarted && !gameOver) {
      loadNextWord(words, currentWordIndex);
    }
  }, [words, gameStarted, currentWordIndex, gameOver, loadNextWord]);

  // Handle letter guess
  const handleLetterGuess = useCallback(
    (letter) => {
      if (!gameStarted || gameOver || guessedLetters.has(letter) || wordCompleted) {
        return;
      }

      const upperLetter = letter.toUpperCase();
      setGuessedLetters((prev) => new Set([...prev, upperLetter]));
      setAttempts((prev) => prev + 1);

      if (upperLetter === missingLetter) {
        // Correct guess! Fill in the word
        const pointsToAdd = 5;
        setScore((prev) => prev + pointsToAdd);
        onPointsChange(pointsToAdd);
        recordGameEvent("filler_word_cleared");
        
        // Show completed word with the letter filled in
        const completedWord = currentWord.split("").join(" ");
        setWordWithBlank(completedWord);
        setWordCompleted(true);
        setMessage(attempts === 0 ? t("perfectFirstTry") : t("correct"));

        // Move to next word after showing the completed word
        setTimeout(() => {
          const nextIndex = currentWordIndex + 1;
          if (nextIndex < words.length) {
            setCurrentWordIndex(nextIndex);
            loadNextWord(words, nextIndex);
          } else {
            // Out of words, but game continues - reset for next cycle
            setWordCompleted(false);
            setMessage(t("greatJob"));
          }
        }, 1500);
      } else {
        // Incorrect guess
        const newWrongGuesses = wrongGuesses + 1;
        setWrongGuesses(newWrongGuesses);
        
        // If 3 wrong guesses, move to next word with 0 points
        if (newWrongGuesses >= 3) {
          setMessage(t("tooManyTries") || "Too many tries! Moving to next word...");
          setTimeout(() => {
            const nextIndex = currentWordIndex + 1;
            if (nextIndex < words.length) {
              setCurrentWordIndex(nextIndex);
              loadNextWord(words, nextIndex);
            } else {
              // Out of words, but game continues
              setWordCompleted(false);
              setMessage(t("greatJob"));
            }
          }, 1500);
        } else {
          setMessage(t("tryAgain"));
        }
      }
    },
    [
      gameStarted,
      gameOver,
      guessedLetters,
      missingLetter,
      attempts,
      currentWordIndex,
      words,
      loadNextWord,
      onPointsChange,
      wordCompleted,
      currentWord,
      wrongGuesses,
      t,
      recordGameEvent,
    ]
  );

  // Handle keyboard input
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!gameStarted || gameOver || wordCompleted) return;
      const key = event.key.toUpperCase();
      // Allow A-Z and German characters Ä, Ö, Ü
      if (/^[A-ZÄÖÜ]$/.test(key) && !guessedLetters.has(key)) {
        handleLetterGuess(key);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [gameStarted, gameOver, guessedLetters, handleLetterGuess, wordCompleted]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="filler-game-container">
      <div className="game-card">
        <h1 className="game-title">{t("fillTheLetter")}</h1>

        <div className="filler-game-info">
          <div className="timer-display">
            <span className="timer-label">{t("time")}</span>
            <span className={`timer-value ${timeLeft <= 10 ? "timer-warning" : ""}`}>
              {formatTime(timeLeft)}
            </span>
          </div>
          <div className="score-display">
            <span className="score-label">{t("score")}</span>
            <span className="score-value">⭐ {score}</span>
          </div>
        </div>

        {message && (
          <div className={`filler-message ${message.includes("✅") || message.includes("🎉") ? "success" : message.includes("❌") ? "error" : "info"}`}>
            {message}
          </div>
        )}

        {gameOver ? (
          <div className="filler-game-over">
            <h2>{t("gameOverTitle")}</h2>
            <p className="final-score">{t("finalScore")} {score} {t("points")}</p>
            <p className="final-words">{t("wordsCompleted")} {currentWordIndex}</p>
            <button className="new-game-button" onClick={startGame}>
              🎮 {t("playAgain")}
            </button>
          </div>
        ) : !gameStarted ? (
          <div className="filler-start-screen">
            <p className="game-instructions">
              {t("gameInstructions").split('\n').map((line, i, arr) => (
                <React.Fragment key={i}>
                  {line}
                  {i < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
            <button className="new-game-button" onClick={startGame}>
              🎮 {t("startGame")}
            </button>
          </div>
        ) : (
          <>
            <div className="word-section">
              <div className="filler-word-display">
                {wordWithBlank || t("loading")}
              </div>
            </div>

            <div className="attempts-display">
              {t("attempts")} {attempts}
            </div>

            <Keyboard
              guessedLetters={guessedLetters}
              onLetterClick={handleLetterGuess}
              disabled={gameOver || wordCompleted}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default FillerGame;

